import { useMemo, useState } from "react";
import { connect } from "react-redux";
import { Container, Grid, IconButton, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DataTable, { TableColumn } from "react-data-table-component";
import { MdAdd } from "react-icons/md";

import { useAppDispatch, useAppSelector } from "../../hooks";
import { history } from "../..";
import { clearUser, getUsers, setUsers } from "../../store/userStore";
import userService from "../../services/userService";
import { User } from "../../interface/api/User";
import { SetUsersAction } from "../../interface/redux-types/SetUsersAction";
import { getRoleName } from "../../utils/constants";
import PageHero from "../shared/page-hero";
import DeleteDialog from "../shared/dialog/delete";
import { tableCustomStyles } from "../../utils/table-styles";
import useTimeout from "../../hooks/useTimeout";
import { ActionIconGroup, DeleteIcon, EditIcon } from "../shared/icons";
import PaginatedTableTop, { paginationTopClassName } from "../shared/paginated-table-top";

const useStyles = makeStyles({
  addIconButton: {
    border: "1px solid rgba(0, 0, 0, 0.23)",
  },
});

const UsersView = ({}) => {
  const classes = useStyles();

  const users = useAppSelector(getUsers);
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Partial<User>>({});
  const onClose = () => setOpen(false);

  useTimeout(() => dispatch(clearUser()), 100);

  useMemo(() => {
    userService.getUsers().then((response) => {
      if (response.ok) {
        const users = response.data as User[];
        const setUsersAction: SetUsersAction = {
          users,
        };
        dispatch(setUsers(setUsersAction));
      }
    });
  }, []);

  const columns: TableColumn<User>[] = [
    { name: "ID", selector: (row) => row.id, sortable: true, width: "80px" },
    { name: "Username", selector: (row) => row.username, sortable: true },
    { name: "First Name", selector: (row) => row.firstName ?? "", sortable: true },
    { name: "Last Name", selector: (row) => row.lastName ?? "", sortable: true },
    { name: "Password", selector: () => "******" },
    { name: "Role", selector: (row) => getRoleName(row.roleId), sortable: true },
    { name: "Phone Number", selector: (row) => row.phoneNumber ?? "" },
    {
      name: "Actions",
      width: "120px",
      cell: (row) => (
        <ActionIconGroup spread>
          <EditIcon onClick={() => history.push(`/user/${row.id}`)} />
          <DeleteIcon
            onClick={() => {
              setSelectedItem(row);
              setOpen(true);
            }}
          />
        </ActionIconGroup>
      ),
    },
  ];

  const onConfirmDelete = () => {
    if (selectedItem && selectedItem.id) {
      userService.deleteUser(selectedItem.id).then((response) => {
        if (response.ok) {
          const setUsersAction: SetUsersAction = {
            users: users.filter((u) => u.id !== selectedItem.id),
          };
          dispatch(setUsers(setUsersAction));
        }
      });
    }

    onClose();
  };

  return (
    <>
      <Container maxWidth="xl">
        <PageHero
          title="Users"
          subtitle="Add and Manage Users."
          showBackButton={false}
          action={
            <Tooltip title="Add User">
              <IconButton
                color="primary"
                className={classes.addIconButton}
                onClick={() => history.push(`/user/new`)}
              >
                <MdAdd size={24} />
              </IconButton>
            </Tooltip>
          }
        />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <PaginatedTableTop>
              <DataTable
                columns={columns}
                data={users ?? []}
                customStyles={tableCustomStyles}
                className={paginationTopClassName}
                striped
                highlightOnHover
                responsive
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[25, 50, 100, 200]}
              />
            </PaginatedTableTop>
          </Grid>
        </Grid>
      </Container>
      <DeleteDialog
        open={open}
        onClose={onClose}
        onConfirmDelete={onConfirmDelete}
      />
    </>
  );
};

export default connect()(UsersView);
